/**
 * NumberedStep - Collapsible step indicator component
 *
 * Used in deployment modals to show numbered steps with optional collapsibility.
 */

import React, { useState } from "react";

interface NumberedStepProps {
    number: number;
    title: string;
    children?: React.ReactNode;
    collapsible?: boolean;
    defaultExpanded?: boolean;
}

export function NumberedStep({
    number,
    title,
    children,
    collapsible = false,
    defaultExpanded = true
}: NumberedStepProps) {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    return (
        <div className="mb-4 rounded-lg border border-purple-100 p-3">
            <div
                className={`flex items-center justify-start gap-3 ${collapsible ? "cursor-pointer select-none" : ""}`}
                onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
            >
                <div className="flex !h-8 !w-8 items-center justify-center rounded-full bg-purple-100">
                    <span className="w-8 text-center text-sm font-bold text-purple-700">
                        {number}
                    </span>
                </div>
                <div className="flex flex-1 items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                    {collapsible && (
                        <div className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-purple-100">
                            <i
                                className={`fas fa-chevron-${isExpanded ? "up" : "down"} text-sm text-purple-600`}
                            ></i>
                        </div>
                    )}
                </div>
            </div>
            {(!collapsible || isExpanded) && children && (
                <div className="mt-3 pl-11">{children}</div>
            )}
        </div>
    );
}
